import * as Sentry from '@sentry/react';

/**
 * Logger utility for controlled logging
 * Prevents debug logs in production and provides consistent formatting
 * Automatically sends errors to Sentry when configured
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  action?: string;
  [key: string]: any;
}

class Logger {
  private isDevelopment: boolean;
  private isSentryEnabled: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isSentryEnabled = !!process.env.REACT_APP_SENTRY_DSN;
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? ` ${JSON.stringify(context)}` : '';
    return `[${timestamp}] [${level.toUpperCase()}] ${message}${contextStr}`;
  }

  private shouldLog(level: LogLevel): boolean {
    // In production, only log warnings and errors
    if (!this.isDevelopment) {
      return level === 'warn' || level === 'error';
    }
    return true;
  }

  private addBreadcrumb(message: string, level: LogLevel, context?: LogContext): void {
    if (this.isSentryEnabled) {
      Sentry.addBreadcrumb({
        message,
        level: level === 'error' ? 'error' : level === 'warn' ? 'warning' : 'info',
        category: context?.component || 'app',
        data: context,
      });
    }
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatMessage('debug', message, context));
    }
    // Add breadcrumb even in production for context when errors occur
    this.addBreadcrumb(message, 'debug', context);
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message, context));
    }
    this.addBreadcrumb(message, 'info', context);
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message, context));
    }
    
    // Send warning to Sentry
    if (this.isSentryEnabled) {
      Sentry.captureMessage(message, {
        level: 'warning',
        extra: context,
      });
    }
  }

  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (this.shouldLog('error')) {
      const errorContext = {
        ...context,
        error: error instanceof Error ? {
          message: error.message,
          stack: error.stack,
          name: error.name,
        } : error,
      };
      console.error(this.formatMessage('error', message, errorContext));
    }

    // Send error to Sentry
    if (this.isSentryEnabled) {
      if (error instanceof Error) {
        Sentry.captureException(error, {
          extra: {
            message,
            ...context,
          },
          tags: {
            component: context?.component,
            action: context?.action,
          },
        });
      } else {
        Sentry.captureMessage(message, {
          level: 'error',
          extra: {
            error,
            ...context,
          },
        });
      }
    }
  }

  // Helper for API errors
  apiError(endpoint: string, error: unknown, context?: LogContext): void {
    const errorMessage = error instanceof Error ? error.message : 'Unknown API error';
    
    this.error(`API Error: ${endpoint}`, error as Error, {
      ...context,
      endpoint,
      type: 'api_error',
    });
  }

  // Helper for user actions (tracking breadcrumbs)
  userAction(action: string, context?: LogContext): void {
    this.info(`User Action: ${action}`, {
      ...context,
      type: 'user_action',
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for external use
export type { LogContext };
